import { showHttpError, showSuccess } from '@calendly/alert'
import { I18n } from '@calendly/i18n'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { updateSchedulingFetchEventTypesCacheData } from 'features/scheduling'
import { rootApi } from 'root-api'

import { fetchEventType, updateEventType } from './api'
import { eventTypeParticipantsSelector, eventTypeSelector } from './selectors'
import {
  eventTypeAccessGrantToParticipant,
  eventTypeMembershipToParticipant,
  organizationMembershipToParticipant,
  serializeOlp
} from './utils'

export const closeModal = createAsyncThunk('objectLevelPermissions/closeModal', () => {
})

export const closeModalWithStoreIntact = createAsyncThunk('objectLevelPermissions/closeModalWithStoreIntact', () => {
})

export const fetchEventTypeData = createAsyncThunk(
  'objectLevelPermissions/fetchEventType',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const eventType = eventTypeSelector(getState())

    if (!eventType.id || !!eventType.disableFetch) return eventType

    const [success, response] = await fetchEventType(eventType)

    if (!success) {
      dispatch(showHttpError(response))
      return rejectWithValue(response)
    }

    return response
  }
)

const updateEventTypeData = (eventType, participants, updateLocation) => {
  const olp = serializeOlp(eventType, participants, updateLocation)
  return updateEventType(eventType, olp)
}

export const submitModal = createAsyncThunk(
  'objectLevelPermissions/submitModal',
  async ({ onSubmit, onSuccess, profileScopeType = null, profilePage = null }, { dispatch, getState }) => {
    const state = getState()
    const eventType = eventTypeSelector(state)
    const participants = eventTypeParticipantsSelector(state)

    const [success, response] = await updateEventTypeData(eventType, participants, 'permissions_modal')
    if (success) {
      const data = { memberships: response.memberships, access_grants: response.access_grants }

      if (onSuccess) { onSuccess({ id: eventType.id, data }) }

      if (profileScopeType && profilePage) {
        dispatch(updateSchedulingFetchEventTypesCacheData({
          api: rootApi, scopeType: profileScopeType, profilePage, uuid: response.uuid, data
        }))
      }

      dispatch(showSuccess(I18n.t('publisher.flash.saved')))
      dispatch(closeModal())
    } else {
      dispatch(showHttpError(response))
    }
  }
)

const organizationMembershipsInit = {
  members: [],
  isLoading: true
}


const initialState = {
  isOpen: false,
  isSubmitting: false,
  eventType: null,
  eventTypeParticipants: [],
  isTouched: false,
  organizationMemberships: organizationMembershipsInit,
  initialParticipants: {},
  newOwner: null
}

const shouldRemoveParticipant = (participant, payload) => {
  if (payload.user_id) {
    return participant.principleType === 'User' && participant.principleUuid === payload.user_uuid
  }

  return participant.principleType === 'OrganizationInvitation' && participant.principleUuid === payload.uuid
}

const slice = createSlice({
  name: 'objectLevelPermissions',
  initialState,
  reducers: {
    openModal(state, { payload }) {
      state.isOpen = true
      state.eventType = payload
    },
    addParticipant(state, { payload }) {
      const participant = organizationMembershipToParticipant(payload)
      const isNewCollectiveOrRoundRobin = !state.eventType?.id && (state.eventType.isCollective || state.eventType.isRoundRobin)

      state.eventTypeParticipants.push({
        ...participant,
        isHost: state.eventType.owner_type === 'Organization',
        eventTypeMembership: isNewCollectiveOrRoundRobin
          ? { ...participant.eventTypeMembership, priority: 0, enabled: true, _destroy: false }
          : participant.eventTypeMembership
      })
      state.isTouched = false
    },
    removeParticipant(state, { payload }) {
      state.eventTypeParticipants = state.eventTypeParticipants.filter(
        ({ principleType, principleUuid }) => !shouldRemoveParticipant({ principleType, principleUuid }, payload)
      )
    },
    setPermissionRole(state, { payload }) {
      state.eventTypeParticipants = state.eventTypeParticipants.map(
        (participant) => (
          participant.principleType === payload.type && participant.principleUuid === payload.uuid
            ? {
              ...participant,
              role: payload.value,
            }
            : participant
        )
      )
    },
    openTransferOwnershipModal(state, { payload }) {
      state.newOwner = payload
    },
    closeTransferOwnershipModal(state) {
      state.newOwner = null
    },
    confirmTransferOwnershipModal(state) {
      state.eventTypeParticipants = state.eventTypeParticipants.map(
        (participant) => {
          if (participant.principleUuid === state.newOwner.user_uuid) {
            return { ...participant, role: 'owner' }
          }
          if (participant.role === 'owner') {
            return { ...participant, role: 'editor' }
          }

          return participant
        }
      )

      state.eventType.owner_uuid = state.newOwner.user_uuid

      state.isTouched = false
      state.newOwner = null
    },
    setTouched(state, { payload }) {
      state.isTouched = payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchEventTypeData.fulfilled, (state, { payload: eventType }) => {
      const participants = eventType.memberships?.map(eventTypeMembershipToParticipant) || []

      eventType.access_grants.forEach((accessGrant) => {
        const participant = participants.find(
          ({ principleType, principleUuid }) => principleType === accessGrant.principle_type
            && principleUuid === accessGrant.principle_uuid
        )

        if (participant) {
          participant.role = accessGrant.role
        } else {
          participants.push(eventTypeAccessGrantToParticipant(accessGrant))
        }
      })

      state.eventType = eventType
      state.eventTypeParticipants = participants
      const organizationMembershipIds = participants
        .map(({ organizationMembershipId }) => organizationMembershipId)
        .filter(Boolean)
      const organizationInvitationIds = participants
        .map(({ organizationInvitationId }) => organizationInvitationId)
        .filter(Boolean)
      state.initialParticipants = { organizationMembershipIds, organizationInvitationIds }
    })
    builder.addCase(submitModal.pending, (state) => {
      state.isSubmitting = true
    })
    builder.addCase(submitModal.fulfilled, (state) => {
      state.isSubmitting = false
    })
    builder.addCase(submitModal.rejected, (state) => {
      state.isSubmitting = false
    })
    builder.addCase(closeModal.fulfilled, () => initialState)
    builder.addCase(closeModalWithStoreIntact.fulfilled, (state) => {
      state.initialParticipants = initialState.initialParticipants
      state.isOpen = initialState.isOpen
      state.isSubmitting = initialState.isSubmitting
      state.isTouched = initialState.isTouched
      state.newOwner = initialState.newOwner
      state.organizationMemberships = initialState.organizationMemberships
    })
  }
})

export const {
  reducer,
  actions: {
    openModal,
    addParticipant,
    removeParticipant,
    setPermissionRole,
    openTransferOwnershipModal,
    closeTransferOwnershipModal,
    confirmTransferOwnershipModal,
    setTouched,
  }
} = slice
